// ExpensePage.tsx
import React, {FC, useEffect, useState, useCallback} from 'react'
import {Form, Formik, FormikProps} from 'formik'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {EXPENSE_TYPE_OPTIONS, IExpenseDefinitionModel, initialExpenseDefinition, validationExpenseDefinition} from './ExpenseModel'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {createExpense,  deleteExpense,  loadExpenses, updateExpense} from './Api'
import FormTabHeader, {TabOption} from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import {ExpenseTable} from './ExpenseTable'

const ExpensePage: FC = () => {
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [expenses, setExpenses] = useState<IExpenseDefinitionModel[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [tab, setTab] = useState<string | 'form'>('form')
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [error, setError] = useState<string | null>(null)

  const tabOptions: TabOption<'form' | 'detail' | 'list'>[] = [
    {key: 'form', title: 'Gider Tanımlama', icon: 'bi bi-geo-alt'},
    {key: 'list', title: 'Gider Listesi', icon: 'bi bi-list'},
  ]

  const fetchExpenses = useCallback(
    async (page: number = 0, sort?: string, order?: 'asc' | 'desc') => {
      setLoading(true)
      setError(null)

      try {
        const response = await loadExpenses(page, 5, sort, order)
        if (response.data.hasError) {
          throw new Error(response.data.message || 'Failed to fetch expenses')
        }
        setExpenses(response.data.payload.content)
        setTotalPages(response.data.payload.totalPages)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred')
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // İlk yükleme
  useEffect(() => {
    if (tab === 'list') {
      fetchExpenses(0)
    }
  }, [tab, fetchExpenses])

  const handleSave = async (values: IExpenseDefinitionModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateExpense({...values, id: selectedId})
        showSuccessToast('Gider Tanımı başarıyla güncellendi')
      } else {
        await createExpense(values)
        showSuccessToast('Gider Tanımı başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      await fetchExpenses(0)
      resetForm()
      setSelectedId(null)
      setTab('list')
    } catch (err) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = useCallback((expense: IExpenseDefinitionModel, formik: FormikProps<IExpenseDefinitionModel>) => {
    formik.setValues(expense)
    setSelectedId(expense.id)
    setTab('form')
  }, [])

  const handleDeleteClick = useCallback((expense: IExpenseDefinitionModel) => {
    setSelectedId(expense.id)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (selectedId) {
      setLoading(true)
      try {
        await deleteExpense(selectedId)
        showSuccessToast('Gider Tanımı başarıyla silindi')
        await fetchExpenses(currentPage)
      } catch (err) {
        console.error(err)
        showErrorToast('Gider Tanımı silinirken bir hata oluştu')
      } finally {
        setLoading(false)
        setShowModal(false)
        setSelectedId(null)
      }
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchExpenses(newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, fetchExpenses]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchExpenses(currentPage, column, order)
    },
    [currentPage, fetchExpenses]
  )

  return (
    <>
      <Formik
        initialValues={initialExpenseDefinition}
        validationSchema={validationExpenseDefinition}
        enableReinitialize
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
            setTab('form')
          }

          return (
            <>
              <FormTabHeader
                formId='expenseForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
              />
              <div className='card mb-5'> 
                {/* Form ve Detail Tabları */}
                {(tab === 'form') && (
                  <Form id='expenseForm' className='form card-body'>
                    {tab === 'form' && (
                      <>
                        <div className='row mb-6'>
                          <SelectInput
                            name='expenseType'
                            label='Gider Türü'
                            options={EXPENSE_TYPE_OPTIONS}
                            wrapperClassName='col-md-6'
                          />
                          <TextInput
                            name='name'
                            label='Gider Tanımı'
                            wrapperClassName='col-md-6'
                          />
                        </div>
                       
                       
                       
                       
                              
                        
                      </>
                    )}
            
                  </Form>
                )}
              </div>
              {/* Liste Tabı */}
              {tab === 'list' && (
                <div className='card'> 
                  <div className='card-body pt-0'>
                    <ExpenseTable
                      data={expenses}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      onEdit={(expense) => handleEdit(expense, formik)}
                      onDelete={handleDeleteClick}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                      loading={loading}
                    />
                  </div>
                </div>
              )}
            </>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu gider tanımını silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export {ExpensePage}
